import React from 'react';
import {Box, Checkbox, IconButton, Menu, MenuItem, TableSortLabel} from '@mui/material';
import {MoreHoriz, FilterAltOutlined} from '@mui/icons-material';
import {useListContext, useTranslate} from 'react-admin';
import {t} from '../../themes/statamicTokens';
import EmptyState from './EmptyState';
import {SkeletonList} from './Skeleton';

const RowKebab = ({actions, record}) => {
    const [anchor, setAnchor] = React.useState(null);
    const open = Boolean(anchor);

    return (
        <>
            <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
                sx={{
                    color: t.color.text.muted,
                    '&:hover': { color: t.color.text.primary, backgroundColor: t.color.sidebarHover },
                }}
            >
                <MoreHoriz fontSize="small"/>
            </IconButton>
            <Menu
                anchorEl={anchor}
                open={open}
                onClose={() => setAnchor(null)}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 0.5,
                            minWidth: 180,
                            borderRadius: `${t.radius.md}px`,
                            border: `1px solid ${t.color.border.default}`,
                            boxShadow: t.shadow.dropdown,
                        },
                    },
                }}
            >
                {(actions ?? []).map((a) => (
                    <MenuItem
                        key={a.label}
                        onClick={() => { a.onClick?.(record); setAnchor(null); }}
                        sx={{
                            fontSize: `${t.type.small.size}px`,
                            color: a.variant === 'danger' ? t.color.status.dangerSolid : t.color.text.primary,
                        }}
                    >
                        {a.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

const StatamicTable = ({columns, rowActions, onRowClick}) => {
    const {
        data = [],
        isLoading,
        selectedIds = [],
        onSelect,
        onToggleItem,
        sort,
        setSort,
    } = useListContext();
    const translate = useTranslate();

    const allSelected = data.length > 0 && selectedIds.length === data.length;
    const someSelected = selectedIds.length > 0 && !allSelected;

    const toggleAll = () => {
        if (allSelected) onSelect?.([]);
        else onSelect?.(data.map(r => r.id));
    };

    const handleSort = (field) => {
        if (!setSort) return;
        const order = sort?.field === field && sort?.order === 'ASC' ? 'DESC' : 'ASC';
        setSort({ field, order });
    };

    return (
        <Box component="table" sx={{width: '100%', borderCollapse: 'collapse'}}>
            <Box component="thead" sx={{backgroundColor: t.color.surface}}>
                    <Box component="tr" sx={{height: t.table.headerHeight}}>
                        <Box
                            component="th"
                            sx={{
                                width: 44,
                                textAlign: 'left',
                                px: 1.5,
                                borderBottom: `1px solid ${t.color.border.default}`,
                            }}
                        >
                            <Checkbox
                                size="small"
                                checked={allSelected}
                                indeterminate={someSelected}
                                onChange={toggleAll}
                                sx={{p: 0.25}}
                            />
                        </Box>
                        {columns.map((col) => (
                            <Box
                                key={col.key}
                                component="th"
                                sx={{
                                    textAlign: 'left',
                                    px: `${t.table.cellPaddingX}px`,
                                    fontSize: `${t.type.caption.size}px`,
                                    fontWeight: 600,
                                    color: t.color.text.secondary,
                                    textTransform: 'none',
                                    borderBottom: `1px solid ${t.color.border.default}`,
                                    whiteSpace: 'nowrap',
                                    width: col.width,
                                }}
                            >
                                {col.sortable ? (
                                    <TableSortLabel
                                        active={sort?.field === col.sortField}
                                        direction={sort?.order?.toLowerCase() ?? 'asc'}
                                        onClick={() => handleSort(col.sortField)}
                                        sx={{
                                            fontSize: `${t.type.caption.size}px`,
                                            fontWeight: 600,
                                            color: `${t.color.text.secondary} !important`,
                                            '& .MuiTableSortLabel-icon': { opacity: sort?.field === col.sortField ? 1 : 0.3 },
                                        }}
                                    >
                                        {col.label}
                                    </TableSortLabel>
                                ) : col.label}
                            </Box>
                        ))}
                        {rowActions && (
                            <Box
                                component="th"
                                sx={{
                                    width: 56,
                                    borderBottom: `1px solid ${t.color.border.default}`,
                                }}
                            />
                        )}
                    </Box>
                </Box>

                <Box component="tbody">
                    {isLoading && data.length === 0 && (
                        <SkeletonList
                            rows={6}
                            columns={columns.length}
                            hasCheckbox
                            hasActions={Boolean(rowActions)}
                        />
                    )}

                    {!isLoading && data.length === 0 && (
                        <Box component="tr">
                            <Box component="td" colSpan={columns.length + (rowActions ? 2 : 1)} sx={{p: 0}}>
                                <EmptyState
                                    icon={<FilterAltOutlined sx={{fontSize: 28, color: t.color.text.muted}}/>}
                                    title={translate('No results')}
                                    description={translate('Try adjusting filters or search, or clear the scope from the sidebar.')}
                                    dense
                                />
                            </Box>
                        </Box>
                    )}

                    {data.map((record, i) => {
                        const selected = selectedIds.includes(record.id);
                        return (
                            <Box
                                key={record.id}
                                component="tr"
                                onClick={() => onRowClick?.(record)}
                                sx={{
                                    height: t.table.rowHeight,
                                    cursor: onRowClick ? 'pointer' : 'default',
                                    backgroundColor: selected ? t.color.status.infoTint : 'transparent',
                                    '&:hover': { backgroundColor: selected ? t.color.status.infoTint : t.color.sidebarHover },
                                    '& > td': {
                                        borderBottom: i === data.length - 1 ? 'none' : `1px solid ${t.color.border.hairline}`,
                                    },
                                }}
                            >
                                <Box component="td" sx={{px: 1.5}} onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                        size="small"
                                        checked={selected}
                                        onChange={() => onToggleItem?.(record.id)}
                                        sx={{p: 0.25}}
                                    />
                                </Box>
                                {columns.map((col) => (
                                    <Box
                                        key={col.key}
                                        component="td"
                                        sx={{
                                            px: `${t.table.cellPaddingX}px`,
                                            fontSize: `${t.type.body.size}px`,
                                            color: t.color.text.primary,
                                            verticalAlign: 'middle',
                                        }}
                                    >
                                        {col.render(record)}
                                    </Box>
                                ))}
                                {rowActions && (
                                    <Box component="td" sx={{pr: 1.5, textAlign: 'right'}} onClick={(e) => e.stopPropagation()}>
                                        <RowKebab actions={rowActions(record)} record={record}/>
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
    );
};

export default StatamicTable;
